import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import SubscriptionStatus from '../components/SubscriptionStatus';
import PricingTable from '../components/PricingTable';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { useNavigate, useSearchParams } from 'react-router-dom';

const SubscriptionManagementPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentTier, setCurrentTier] = useState('free');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const checkoutStarted = useRef(false);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  useEffect(() => {
    const tier = searchParams.get('tier');
    if (tier === 'agency_unlimited' && !loading && !checkoutStarted.current) {
      checkoutStarted.current = true;
      startAgencyUnlimitedCheckout();
    } else if (tier === 'citation_tracker' && !loading && !checkoutStarted.current) {
      checkoutStarted.current = true;
      navigate('/citation-tracker/onboard');
    }
  }, [searchParams, loading]);

  const startAgencyUnlimitedCheckout = async () => {
    try {
      setCheckoutLoading(true);
      const origin = window.location.origin;
      const response = await api.post('/subscription/create-checkout', {
        tier: 'agency_unlimited',
        successUrl: `${origin}/?payment_success=true`,
        cancelUrl: `${origin}/account/upgrade?tier=agency_unlimited`
      });

      const checkoutUrl = response.success
        ? response.data?.url
        : response.data?.data?.url;

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }

      console.error('Failed to create checkout session:', response);
      alert('Failed to start checkout. Please try again or contact support.');
      setCheckoutLoading(false);
    } catch (err) {
      console.error('Error starting agency unlimited checkout:', err);
      alert('Failed to start checkout. Please try again or contact support.');
      setCheckoutLoading(false);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/subscription/status');
      console.log('SubscriptionManagementPage - API response:', response);
      
      // Handle both response structures
      if (response.success) {
        const subscriptionData = response.data?.subscription || response.subscription;
        console.log('SubscriptionManagementPage - Subscription data:', subscriptionData);
        setSubscription(subscriptionData);
        setCurrentTier(subscriptionData?.tier || 'free');
        console.log('SubscriptionManagementPage - Current tier set to:', subscriptionData?.tier || 'free');
      } else if (response.data?.success) {
        const subscriptionData = response.data.data.subscription;
        console.log('SubscriptionManagementPage - Subscription data (nested):', subscriptionData);
        setSubscription(subscriptionData);
        setCurrentTier(subscriptionData?.tier || 'free');
        console.log('SubscriptionManagementPage - Current tier set to:', subscriptionData?.tier || 'free');
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    // Option 1: Use static Stripe portal link (simpler but less secure)
    if (process.env.REACT_APP_STRIPE_CUSTOMER_PORTAL) {
      window.open(process.env.REACT_APP_STRIPE_CUSTOMER_PORTAL, '_blank');
      return;
    }
    
    // Option 2: Use dynamic portal session (more secure, requires configuration)
    try {
      console.log('Opening customer portal...');
      const response = await api.post('/subscription/customer-portal', {
        returnUrl: window.location.href
      });
      
      console.log('Portal response:', response);
      
      // Check for success in the response
      if (response.success) {
        // Direct success response
        console.log('Opening portal in new window:', response.data.url);
        window.open(response.data.url, '_blank');
      } else if (response.data && response.data.success) {
        // Nested success response
        console.log('Opening portal in new window:', response.data.data.url);
        window.open(response.data.data.url, '_blank');
      } else {
        console.error('Portal request failed:', response.data || response);
        console.error('Full response:', response);
        const errorMsg = response.data?.error || response.error || 'Unknown error';
        alert(`Error: ${errorMsg}`);
      }
    } catch (err) {
      console.error('Failed to open customer portal:', err);
      alert(`Failed to open billing portal: ${err.message}`);
    }
  };

  const handleCancelSubscription = async (immediately = false) => {
    try {
      setCancelLoading(true);
      const response = await api.post('/subscription/cancel', { immediately });
      
      if (response.data.success) {
        setShowCancelModal(false);
        await fetchSubscriptionStatus();
        
        if (immediately) {
          alert('Your subscription has been cancelled immediately.');
        } else {
          alert(`Your subscription will end on ${new Date(response.data.data.currentPeriodEnd).toLocaleDateString()}`);
        }
      }
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      alert('Failed to cancel subscription. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivate = async () => {
    try {
      const response = await api.post('/subscription/reactivate');
      
      if (response.data.success) {
        await fetchSubscriptionStatus();
        alert('Your subscription has been reactivated!');
      }
    } catch (err) {
      console.error('Failed to reactivate subscription:', err);
      alert('Failed to reactivate subscription. Please try again.');
    }
  };

  if (loading || checkoutLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          {checkoutLoading && (
            <p className="text-muted-foreground">Starting checkout...</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="card">
        {/* Header */}
        <div className="card-header">
          <button
            onClick={() => navigate("/")}
            className="flex items-center text-muted-foreground hover:text-foreground mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back
          </button>

          <h1 className="text-3xl font-bold text-foreground">
            Subscription Management
          </h1>
          <p className="mt-2 text-muted-foreground">
            Manage your subscription plan and billing
          </p>
        </div>

        {/* Pricing Table for Upgrades/Downgrades */}
        <div className="card-body">
          <h2 className="text-2xl font-bold text-foreground mb-8">
            {currentTier === "free" ? "Choose Your Plan" : "Change Your Plan"}
          </h2>
          <PricingTable currentTier={currentTier} />
        </div>

        {/* Current Subscription Status */}
        <div className="card-body">
          <SubscriptionStatus />
        </div>

        {/* Subscription Management Actions */}
        {subscription && subscription.tier !== 'free' && subscription.status === 'active' && (
          <div className="card-body border-t border-border">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Subscription Actions
            </h3>
            
            <div className="space-y-4">
              {/* Stripe Customer Portal */}
              <div className="bg-accent border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">
                  Manage Billing & Payment Method
                </h4>
                <p className="text-blue-700 text-sm mb-3">
                  Update your payment method, download invoices, and view billing history.
                </p>
                <button
                  onClick={handleManageSubscription}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm"
                >
                  Open Billing Portal
                </button>
              </div>

              {/* Cancel Subscription */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-900 mb-2">
                  Cancel Subscription
                </h4>
                <p className="text-red-700 text-sm mb-3">
                  Cancel your subscription. You can choose to cancel immediately or at the end of your billing period.
                </p>
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors text-sm"
                >
                  Cancel Subscription
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reactivation for cancelled subscriptions */}
        {subscription && subscription.cancelAtPeriodEnd && (
          <div className="card-body border-t border-border">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-medium text-yellow-900 mb-2">
                Subscription Scheduled for Cancellation
              </h4>
              <p className="text-yellow-700 text-sm mb-3">
                Your subscription will end on {new Date(subscription.currentPeriodEnd).toLocaleDateString()}. 
                You can reactivate it to continue your service.
              </p>
              <button
                onClick={handleReactivate}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors text-sm"
              >
                Reactivate Subscription
              </button>
            </div>
          </div>
        )}

        {/* Cancel Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-background0 bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-card rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-medium text-foreground mb-4">
                Cancel Subscription
              </h3>

              <p className="text-muted-foreground mb-6">
                Are you sure you want to cancel your subscription? You can
                choose to cancel immediately or at the end of your billing
                period.
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => handleCancelSubscription(false)}
                  disabled={cancelLoading}
                  className="w-full bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700 transition-colors disabled:opacity-50"
                >
                  Cancel at Period End
                </button>

                <button
                  onClick={() => handleCancelSubscription(true)}
                  disabled={cancelLoading}
                  className="w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  Cancel Immediately
                </button>

                <button
                  onClick={() => setShowCancelModal(false)}
                  disabled={cancelLoading}
                  className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Keep Subscription
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionManagementPage;